import React, { useState, useEffect } from 'react';
import {
  Plus, Search, FileText, Clock, CheckCircle, XCircle,
  RefreshCw, Eye, AlertCircle, Filter, Inbox,
} from 'lucide-react';
import { solicitudesAPI } from '../../../api/solicitudes';
import Pagination from '../../common/Pagination';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';

const PAGE_SIZE = 20;

const ESTADO_CFG = {
  pendiente:   { label: 'Pendiente',   cls: 'bg-amber-100 text-amber-700',  Icon: Clock        },
  en_revision: { label: 'En revisión', cls: 'bg-blue-100 text-blue-600',    Icon: AlertCircle  },
  aprobada:    { label: 'Aprobada',    cls: 'bg-green-100 text-green-700',  Icon: CheckCircle  },
  en_proceso:  { label: 'En proceso',  cls: 'bg-purple-100 text-purple-700',Icon: RefreshCw    },
  completada:  { label: 'Completada',  cls: 'bg-teal-100 text-teal-700',    Icon: CheckCircle  },
  rechazada:   { label: 'Rechazada',   cls: 'bg-red-100 text-red-600',      Icon: XCircle      },
  cancelada:   { label: 'Cancelada',   cls: 'bg-gray-100 text-gray-500',    Icon: XCircle      },
};

const PRIORIDAD_CFG = {
  urgente: 'text-red-600 font-bold',
  alta:    'text-orange-500 font-semibold',
  normal:  'text-gray-500',
  baja:    'text-gray-400',
};

const CATEGORIA_LABELS = {
  certificaciones: 'Certificaciones',
  prestamos:       'Préstamos',
  vacaciones:      'Vacaciones',
  novedades:       'Novedades',
  disciplinarias:  'Disciplinarias',
  quejas:          'Quejas',
  seguro:          'Seguro',
  sistemas:        'Sistemas / IT',
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CFG[estado] || ESTADO_CFG.pendiente;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <cfg.Icon size={10} /> {cfg.label}
    </span>
  );
}

export default function ListaSolicitudes({ currentCompany, currentUser, refreshKey, onSelect, onCrear }) {
  const [solicitudes, setSolicitudes] = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filtroEstado, setFiltroEstado]       = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);

  const { can } = usePermissionsContext();
  const isRRHH = can('REQUEST_APPROVE');

  const cargar = async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        solicitudesAPI.getAll({
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          ...(filtroEstado    && { estado: filtroEstado }),
          ...(filtroCategoria && { categoria: filtroCategoria }),
          ...(search          && { search }),
        }),
        isRRHH ? solicitudesAPI.getStats() : Promise.resolve(null),
      ]);
      setSolicitudes(listRes.solicitudes || []);
      setTotal(listRes.total || 0);
      if (statsRes) setStats(statsRes.stats);
    } catch (err) {
      console.error('Error loading solicitudes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [refreshKey, page, filtroEstado, filtroCategoria]); // eslint-disable-line
  useEffect(() => { setPage(1); cargar(); }, [search]); // eslint-disable-line

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-purple-50 rounded-xl border border-purple-100">
            <FileText size={16} className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-base font-bold text-gray-900">Solicitudes</h1>
            <p className="text-xs text-gray-400">Centro de solicitudes del personal</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={cargar}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={12} /> Actualizar
          </button>
          <button
            onClick={onCrear}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors"
          >
            <Plus size={12} /> Nueva Solicitud
          </button>
        </div>
      </div>

      {/* Stats (solo RRHH) */}
      {isRRHH && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { key: 'pendiente',  label: 'Pendiente',  color: 'text-amber-600' },
            { key: 'en_proceso', label: 'En proceso', color: 'text-purple-600' },
            { key: 'aprobada',   label: 'Aprobadas',  color: 'text-green-600' },
            { key: 'rechazada',  label: 'Rechazadas', color: 'text-red-600'   },
            { key: 'total',      label: 'Total',      color: 'text-gray-700'  },
          ].map(({ key, label, color }) => (
            <div key={key} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className={`text-2xl font-bold ${color}`}>{stats[key] || 0}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
          <input
            type="text"
            placeholder="Buscar por asunto o empleado..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
          />
        </div>

        <select
          value={filtroEstado}
          onChange={e => { setFiltroEstado(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-purple-400"
        >
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_CFG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select
          value={filtroCategoria}
          onChange={e => { setFiltroCategoria(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-purple-400"
        >
          <option value="">Todas las categorías</option>
          {Object.entries(CATEGORIA_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      ) : solicitudes.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center justify-center py-20">
          <Inbox size={40} className="text-gray-200 mb-3" />
          <p className="text-sm font-medium text-gray-500">No hay solicitudes registradas</p>
          <p className="text-xs text-gray-400 mt-1">Crea tu primera solicitud haciendo clic en "Nueva Solicitud".</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Solicitud</th>
                {isRRHH && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Empleado</th>
                )}
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Categoría</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Prioridad</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {solicitudes.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{s.asunto}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.tipo_nombre}</p>
                  </td>
                  {isRRHH && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {s.empleado_foto ? (
                          <img src={s.empleado_foto} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-xs font-bold text-purple-600">
                              {s.empleado_nombre?.[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 text-xs">{s.empleado_nombre}</p>
                          <p className="text-gray-400 text-xs">{s.empleado_codigo}</p>
                        </div>
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-medium">
                      {CATEGORIA_LABELS[s.categoria] || s.categoria}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs capitalize ${PRIORIDAD_CFG[s.prioridad] || ''}`}>
                      {s.prioridad}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">
                    {new Date(String(s.fecha_solicitud).split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <EstadoBadge estado={s.estado} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onSelect(s)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                    >
                      <Eye size={12} /> Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
